<div class="approval-timeline" [ngClass]="{ 'compact': compact }">
  <!-- Header Section -->
  <div class="timeline-header" *ngIf="showHeader && approvalData">
    <div class="header-content">
      <div class="document-info">
        <h5 class="document-title">
          <mat-icon class="document-icon">description</mat-icon>
          {{ approvalData.document_type }} #{{ approvalData.document_id }}
        </h5>
        <p class="document-meta">
          Created by {{ approvalData.created_by }} on {{ approvalData.created_at | date:'medium' }}
        </p>
      </div>
      <div class="status-badge">
        <span class="badge" [ngClass]="'badge-' + getStatusColor(approvalData.overall_status)">
          {{ approvalData.overall_status | titlecase }}
        </span>
      </div>
    </div>
    
    <!-- Progress Bar -->
    <div class="progress-section">
      <div class="progress-info">
        <span class="progress-text">Approval Progress</span>
        <span class="progress-percentage">{{ getProgressPercentage() | number:'1.0-0' }}%</span>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" [style.width.%]="getProgressPercentage()"></div>
      </div>
      <div class="stage-info">
        <span>Stage {{ approvalData.current_stage }} of {{ approvalData.total_stages }}</span>
      </div>
    </div>

    <!-- Enhanced Data Summary -->
    <div class="enhanced-summary" *ngIf="approvalData.enhanced_data">
      <div class="summary-grid">
        <div class="summary-item">
          <mat-icon class="summary-icon">schedule</mat-icon>
          <div class="summary-content">
            <span class="summary-label">Total Pending Time</span>
            <span class="summary-value">{{ approvalData.enhanced_data.total_pending_time_formatted }}</span>
          </div>
        </div>
        
        <div class="summary-item" *ngIf="hasCancellationInfo()">
          <mat-icon class="summary-icon warning">warning</mat-icon>
          <div class="summary-content">
            <span class="summary-label">Cancellations</span>
            <span class="summary-value">{{ getCancellationInfo()?.count }}</span>
          </div>
        </div>
        
        <div class="summary-item" *ngIf="getTimelineSummary()">
          <mat-icon class="summary-icon">timeline</mat-icon>
          <div class="summary-content">
            <span class="summary-label">Total Actions</span>
            <span class="summary-value">{{ getTimelineSummary()?.total_entries }}</span>
          </div>
        </div>

        <!-- Response Time Analytics -->
        <div class="summary-item" *ngIf="getAverageResponseTime()">
          <mat-icon class="summary-icon">speed</mat-icon>
          <div class="summary-content">
            <span class="summary-label">Avg Response Time</span>
            <span class="summary-value">{{ getAverageResponseTime() }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Rejection Information -->
    <div class="rejection-section" *ngIf="hasRejectionInfo()">
      <div class="rejection-header">
        <mat-icon class="rejection-icon">warning</mat-icon>
        <span class="rejection-title">Rejection History</span>
      </div>
      <div class="rejection-details">
        <div class="rejection-item">
          <span class="rejection-label">Rejection Count:</span>
          <span class="rejection-value">{{ getRejectionInfo()?.count }}</span>
        </div>
        <div class="rejection-item" *ngIf="getRejectionInfo()?.level">
          <span class="rejection-label">Last Rejected at Level:</span>
          <span class="rejection-value">{{ getRejectionInfo()?.level }}</span>
        </div>
        <div class="rejection-item" *ngIf="getRejectionInfo()?.rejectedBy">
          <span class="rejection-label">Rejected By:</span>
          <span class="rejection-value">{{ getRejectionInfo()?.rejectedBy }}</span>
        </div>
      </div>
    </div>

    <!-- Response Time Analytics -->
    <div class="response-analytics" *ngIf="getResponseTimeAnalytics()">
      <div class="analytics-header">
        <mat-icon class="analytics-icon">analytics</mat-icon>
        <span class="analytics-title">Response Time Analytics</span>
      </div>
      <div class="analytics-grid">
        <div class="analytics-item">
          <span class="analytics-label">Fastest Response:</span>
          <span class="analytics-value fast">{{ getFastestResponseTime() }}</span>
        </div>
        <div class="analytics-item">
          <span class="analytics-label">Slowest Response:</span>
          <span class="analytics-value slow">{{ getSlowestResponseTime() }}</span>
        </div>
        <div class="analytics-item">
          <span class="analytics-label">Average Response:</span>
          <span class="analytics-value">{{ getAverageResponseTime() }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- Timeline Stages -->
  <div class="timeline-container" *ngIf="approvalData?.stages">
    <div class="timeline-line"></div>
    
    <div class="timeline-stage" 
         *ngFor="let stage of approvalData.stages; let i = index"
         [ngClass]="{
           'completed': stage.status === 'approved',
           'current': stage.is_current || stage.status === 'pending',
           'rejected': stage.status === 'rejected',
           'upcoming': stage.status === 'not_started'
         }">
      
      <!-- Stage Icon -->
      <div class="stage-icon">
        <mat-icon [ngClass]="'icon-' + getStatusColor(stage.status)">
          {{ getStatusIcon(stage.status) }}
        </mat-icon>
        <div class="stage-number">{{ stage.level }}</div>
      </div>
      
      <!-- Stage Content -->
      <div class="stage-content">
        <div class="stage-header">
          <h6 class="stage-name">{{ stage.name }}</h6>
          <span class="stage-status" [ngClass]="'status-' + getStatusColor(stage.status)">
            {{ stage.status | titlecase }}
          </span>
        </div>
        
        <div class="stage-details">
          <p class="approver" *ngIf="stage.approver_name">
            <mat-icon class="detail-icon">person</mat-icon>
            {{ stage.approver_name }}
          </p>
          
          <p class="pending-time" *ngIf="stage.pending_time_formatted && stage.pending_time_formatted !== '0s'">
            <mat-icon class="detail-icon">schedule</mat-icon>
            Pending: {{ stage.pending_time_formatted }}
          </p>
          
          <p class="cancellation-count" *ngIf="stage.cancellation_count > 0">
            <mat-icon class="detail-icon warning">warning</mat-icon>
            Cancelled {{ stage.cancellation_count }} time(s)
          </p>

          <!-- Response Time Indicator -->
          <div class="response-time-indicator" *ngIf="getStageResponseTime(stage)">
            <mat-icon class="detail-icon">speed</mat-icon>
            <span class="response-time" [ngClass]="getResponseTimeClass(stage)">
              Response: {{ getStageResponseTime(stage) }}
            </span>
          </div>
        </div>

        <!-- Timeline Entries -->
        <div class="timeline-entries" *ngIf="hasTimelineData(stage)">
          <div class="timeline-entry" 
               *ngFor="let entry of stage.timeline; let j = index"
               [ngClass]="'entry-' + getActionColor(entry.action)">
            <div class="entry-header">
              <mat-icon class="entry-icon">{{ getActionIcon(entry.action) }}</mat-icon>
              <span class="entry-action">{{ entry.action | titlecase }}</span>
              <span class="entry-time">{{ entry.timestamp | date:'short' }}</span>
            </div>
            <div class="entry-details">
              <p class="entry-user" *ngIf="entry.user_name">
                <mat-icon class="detail-icon">person</mat-icon>
                {{ entry.user_name }}
              </p>
              <p class="entry-duration" *ngIf="entry.pending_duration">
                <mat-icon class="detail-icon">schedule</mat-icon>
                Duration: {{ formatDuration(entry.pending_duration) }}
              </p>
              <p class="entry-comments" *ngIf="entry.comments">
                <mat-icon class="detail-icon">comment</mat-icon>
                {{ entry.comments }}
              </p>
              <p class="entry-rejection" *ngIf="entry.rejection_reason">
                <mat-icon class="detail-icon danger">cancel</mat-icon>
                Rejection: {{ entry.rejection_reason }}
              </p>
              <p class="entry-cancellation" *ngIf="entry.cancellation_reason">
                <mat-icon class="detail-icon warning">block</mat-icon>
                Cancellation: {{ entry.cancellation_reason }}
              </p>
            </div>
          </div>
        </div>
        
        <!-- Current Stage Indicator -->
        <div class="current-indicator" *ngIf="stage.is_current">
          <mat-icon class="current-icon">priority_high</mat-icon>
          <span>Awaiting Approval</span>
        </div>
      </div>
    </div>
  </div>

  <!-- No Data State -->
  <div class="no-data" *ngIf="!approvalData">
    <mat-icon class="no-data-icon">timeline</mat-icon>
    <p>No approval data available</p>
  </div>

  <!-- Current Stage Summary -->
  <div class="current-stage-summary" *ngIf="getCurrentStage()">
    <div class="summary-card">
      <div class="summary-header">
        <mat-icon class="summary-icon">pending_actions</mat-icon>
        <h6>Current Stage</h6>
      </div>
      <div class="summary-content">
        <p class="current-stage-name">{{ getCurrentStage()?.name }}</p>
        <p class="current-stage-info">Waiting for approval at level {{ getCurrentStage()?.level }}</p>
        <div class="next-stage" *ngIf="getNextStage()">
          <p class="next-stage-label">Next: {{ getNextStage()?.name }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Rejection Details Modal Trigger -->
  <div class="rejection-details-section" *ngIf="hasRejectionInfo()">
    <div class="rejection-details-card">
      <div class="rejection-details-header">
        <mat-icon class="rejection-details-icon">history</mat-icon>
        <h6>Detailed Rejection History</h6>
      </div>
      <div class="rejection-list">
        <div class="rejection-item" *ngFor="let rejection of getDetailedRejections()">
          <div class="rejection-item-header">
            <span class="rejection-level">Level {{ rejection.level }}</span>
            <span class="rejection-date">{{ rejection.date | date:'short' }}</span>
          </div>
          <div class="rejection-item-details">
            <p class="rejection-user">
              <mat-icon class="detail-icon">person</mat-icon>
              {{ rejection.rejectedBy }}
            </p>
            <p class="rejection-reason" *ngIf="rejection.reason">
              <mat-icon class="detail-icon">comment</mat-icon>
              {{ rejection.reason }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</div> 